"use client";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useMonthlyUsageStats } from "@/hooks/use-monthly-usage-stats";
import { UsageEmpty } from "@/components/usage-empty";
import { calculateUsageChange, type UsageChange } from "@/lib/usage-comparison";
import { UsageStatCard } from "@/components/usage-stat-card";
import { UsageUnavailable } from "@/components/usage-unavailable";

interface StatItem extends UsageChange {
  title: string;
  value: string;
  description: string;
}

const formatNumber = (num: number | undefined): string =>
  (num ?? 0).toLocaleString();

export function PropertyVisualizeStats() {
  const { currentStats, previousStats, error, previousError, isLoading, previousLoading } = useMonthlyUsageStats();

  if (error) return <UsageUnavailable />;

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card
            key={i}
            className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-purple-200 animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-purple-200 animate-pulse rounded mb-2" />
              <div className="h-3 w-32 bg-purple-200 animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!currentStats) return <UsageEmpty />;

  const prev = previousStats || {
    propertyVisualize: 0,
    propertyVisualizeUsers: 0,
    propertyVisualizeRestyle: 0,
    propertyVisualizeRestyleUsers: 0,
    propertyVisualizeCleanRoom: 0,
    propertyVisualizeCleanRoomUsers: 0,
    propertyVisualizeCameraAngle: 0,
    propertyVisualizeCameraAngleUsers: 0,
    propertyVisualizeVideo: 0,
    propertyVisualizeVideoUsers: 0,
  };

  const stats: StatItem[] = [
    {
      title: "Properties Visualized",
      value: formatNumber(currentStats.propertyVisualize),
      description: "Property visualizations this month",
      ...calculateUsageChange(
        currentStats.propertyVisualize,
        prev.propertyVisualize
      ),
    },
    {
      title: "Unique Users",
      value: formatNumber(currentStats.propertyVisualizeUsers),
      description: "Users who used Property Visualize",
      ...calculateUsageChange(
        currentStats.propertyVisualizeUsers,
        prev.propertyVisualizeUsers
      ),
    },
    {
      title: "Restyle",
      value: formatNumber(currentStats.propertyVisualizeRestyle),
      description: "Restyle visualizations",
      ...calculateUsageChange(
        currentStats.propertyVisualizeRestyle,
        prev.propertyVisualizeRestyle
      ),
    },
    {
      title: "Clean Room",
      value: formatNumber(currentStats.propertyVisualizeCleanRoom),
      description: "Room cleaning visualizations",
      ...calculateUsageChange(
        currentStats.propertyVisualizeCleanRoom,
        prev.propertyVisualizeCleanRoom
      ),
    },
    {
      title: "Camera Angle",
      value: formatNumber(currentStats.propertyVisualizeCameraAngle),
      description: "Camera angle adjustments",
      ...calculateUsageChange(
        currentStats.propertyVisualizeCameraAngle,
        prev.propertyVisualizeCameraAngle
      ),
    },
    {
      title: "Video",
      value: formatNumber(currentStats.propertyVisualizeVideo),
      description: "Video visualizations",
      ...calculateUsageChange(
        currentStats.propertyVisualizeVideo,
        prev.propertyVisualizeVideo
      ),
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {stats.map((stat) => (
        <UsageStatCard key={stat.title} title={stat.title} value={stat.value}
          description={stat.description} comparison={previousError ? calculateUsageChange(undefined) : stat}
          period="previous month" comparisonLoading={previousLoading} />
      ))}
    </div>
  );
}
